import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { announceReaderScrollIntent } from '../utils/readerScrollIntent';
import { taskDisplayTitle } from '../utils/taskDisplayLabels';

import './ActivityRing.css';
import './TaskChecklist.css';

export interface TaskChecklistItem {
  id: string;
  title: string;
  status: string;
  statusLabel: string;
  statusTitle?: string;
  detail?: React.ReactNode;
  selectable?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  selectionDependency?: boolean;
  completionOnly?: boolean;
  viewable?: boolean;
  activityFrozen?: boolean;
}

interface TaskChecklistProps {
  ariaLabel: string;
  heading: string;
  headingAction?: React.ReactNode;
  totals: React.ReactNode;
  items: TaskChecklistItem[];
  completionOnly?: boolean;
  onSelectionChange?: (taskId: string, checked: boolean) => void;
  onViewTask?: (taskId: string) => void;
  /**
   * Rows drawn at first; the rest wait behind "Show N more", which reveals this
   * many more per click. Unset renders every row with no toggle.
   */
  initialVisibleCount?: number;
}

const displayTitle = (item: TaskChecklistItem): string => taskDisplayTitle(item.title);

const isTaskBusy = (item: TaskChecklistItem): boolean => !item.activityFrozen
  && ['queued', 'ai_in_progress', 'human_in_progress'].includes(item.status);

const TaskChecklistTitle: React.FC<{ item: TaskChecklistItem }> = ({ item }) => {
  const title = displayTitle(item);
  const textRef = useRef<HTMLElement>(null);
  const measureTitle = useCallback(() => {
    const text = textRef.current;
    const label = text?.parentElement;

    if (!text || !label) return;
    if (text.scrollWidth > text.clientWidth) label.title = item.title;
    else label.removeAttribute('title');
  }, [item.title]);

  useEffect(() => {
    measureTitle();
    const observer = typeof globalThis.ResizeObserver === 'function'
      ? new globalThis.ResizeObserver(measureTitle) : null;

    if (textRef.current) observer?.observe(textRef.current);

    return () => observer?.disconnect();
  }, [title, measureTitle]);

  return (
    <span className="phase-task-picker__task-title" tabIndex={0}
      onMouseEnter={measureTitle} onFocus={measureTitle}>
      <span ref={textRef} className="phase-task-picker__task-title-text">{title}</span>
    </span>
  );
};

const TaskChecklistContent: React.FC<{ item: TaskChecklistItem }> = ({ item }) => (
  <>
    <TaskChecklistTitle item={item} />
    <span className={`phase-task-picker__task-status phase-task-picker__task-status--${item.status}`}
      title={item.statusTitle || item.statusLabel}>
      {item.statusLabel}
    </span>
    {item.detail !== undefined && item.detail !== null && (
      <span className="phase-task-picker__task-credit">{item.detail}</span>
    )}
  </>
);

const TaskCompletionMark: React.FC<{ item: TaskChecklistItem }> = ({ item }) => {
  const busy = isTaskBusy(item);
  const statusDescription = busy && item.status !== 'queued'
    ? 'running' : item.status === 'done' ? 'completed' : item.statusLabel;

  return (
    <span className={`phase-task-picker__check phase-task-picker__check--readonly${item.status === 'done' ? ' phase-task-picker__check--done' : ''}${busy ? ' phase-task-picker__check--busy activity-ring' : ''}`}
      role="checkbox" aria-readonly="true" aria-checked={item.status === 'done'} aria-busy={busy || undefined}
      aria-label={`${displayTitle(item)} ${statusDescription}`}>
      <span aria-hidden="true">{item.status === 'done' ? '✓' : item.status === 'error' ? '!' : ''}</span>
    </span>
  );
};

const TaskSelectionDependencyMark: React.FC<{ item: TaskChecklistItem }> = ({ item }) => (
  <span className="phase-task-picker__dependency" role="img"
    aria-label={`${displayTitle(item)} is included with its selected stage`}>
    <i className="fa-solid fa-link" aria-hidden="true" />
  </span>
);

const TaskChecklistSelection: React.FC<{
  item: TaskChecklistItem;
  completionOnly: boolean;
  onSelectionChange?: TaskChecklistProps['onSelectionChange'];
}> = ({ item, completionOnly, onSelectionChange }) => {
  if (completionOnly || isTaskBusy(item)) return (
    <div className="phase-task-picker__task-select phase-task-picker__task-select--disabled">
      <TaskCompletionMark item={item} />
      <TaskChecklistContent item={item} />
    </div>
  );
  if (item.status === 'done') return (
    <div className="phase-task-picker__task-select phase-task-picker__task-select--disabled phase-task-picker__task-select--completed">
      <TaskChecklistContent item={item} />
    </div>
  );
  if (item.selectionDependency) return (
    <label className={`phase-task-picker__task-select phase-task-picker__task-select--dependency${item.selectable ? '' : ' phase-task-picker__task-select--disabled'}`}>
      <input checked={Boolean(item.checked)} disabled={!item.selectable} type="checkbox"
        aria-label={displayTitle(item)}
        onChange={(event) => onSelectionChange?.(item.id, event.target.checked)} />
      <TaskSelectionDependencyMark item={item} />
      <TaskChecklistContent item={item} />
    </label>
  );

  return (
    <label className={`phase-task-picker__task-select${item.selectable ? '' : ' phase-task-picker__task-select--disabled'}`}>
      <input checked={Boolean(item.checked)} disabled={!item.selectable} type="checkbox"
        aria-label={displayTitle(item)} aria-checked={item.indeterminate ? 'mixed' : Boolean(item.checked)}
        ref={(input) => { if (input) input.indeterminate = Boolean(item.indeterminate); }}
        onChange={(event) => onSelectionChange?.(item.id, event.target.checked)} />
      <span className="phase-task-picker__check" aria-hidden="true">
        <i className={`fa-solid ${item.indeterminate ? 'fa-minus' : 'fa-check'}`} />
      </span>
      <TaskChecklistContent item={item} />
    </label>
  );
};

interface TaskChecklistRowsProps {
  id?: string;
  onClickCapture?: React.MouseEventHandler<HTMLDivElement>;
  items: TaskChecklistItem[];
  completionOnly?: boolean;
  onSelectionChange?: TaskChecklistProps['onSelectionChange'];
  onViewTask?: TaskChecklistProps['onViewTask'];
}

/** The rows alone, for a caller that owns its own heading (the running-tasks dock). */
export const TaskChecklistRows: React.FC<TaskChecklistRowsProps> = ({
  id, onClickCapture, items, completionOnly = false, onSelectionChange, onViewTask,
}) => (
  <div className="phase-task-picker__list" id={id} onClickCapture={onClickCapture}>
    {items.map((item) => (
      <div key={item.id}
        className={`phase-task-picker__task${item.checked && item.status !== 'error' && !completionOnly && !item.selectionDependency ? ' phase-task-picker__task--selected' : ''}${item.checked && item.status === 'error' && !completionOnly && !item.selectionDependency ? ' phase-task-picker__task--retry-selected' : ''}${item.selectionDependency && !completionOnly ? ' phase-task-picker__task--dependency' : ''}${!item.selectable && !completionOnly && !item.completionOnly && !item.selectionDependency ? ' phase-task-picker__task--unavailable' : ''}${item.status === 'done' ? ' phase-task-picker__task--completed' : ''}`}>
        <TaskChecklistSelection item={item} completionOnly={completionOnly || Boolean(item.completionOnly)}
          onSelectionChange={onSelectionChange} />
        {onViewTask && item.viewable !== false && (
          <button aria-label={`View task: ${displayTitle(item)}`} className="phase-task-picker__view" type="button"
            onClick={() => onViewTask(item.id)}>
            <i className="fa-solid fa-eye" aria-hidden="true" />
            <span className="phase-task-picker__view-label">View</span>
          </button>
        )}
      </div>
    ))}
  </div>
);

type RowReveal = { kind: 'more'; from: number } | { kind: 'less' };

interface TaskRowWindow {
  /** The reader's page size; undefined draws every row with no toggles (the default). */
  page?: number;
  visibleCount: number;
  hiddenCount: number;
  /** Rows the next "Show N more" reveals: one page, or the smaller remainder. */
  step: number;
}

const ROW_FOCUS_SELECTOR = 'input:not(:disabled), button:not(:disabled), [tabindex="0"]';
/** How long after "Show N more" a repeated click (a double-click's second) is still part of it. */
const FOLLOW_UP_CLICK_MS = 1000;
const TOGGLE_SELECTOR = '.phase-task-picker__more, .phase-task-picker__less';

const normalizePageSize = (value?: number): number | undefined => (
  value === undefined || !Number.isFinite(value) ? undefined : Math.max(1, Math.floor(value))
);

/** Positional window: hidden rows are only not drawn; the caller still owns (and totals) all of them. */
const taskRowWindow = (total: number, requested: number, page?: number): TaskRowWindow => {
  if (page === undefined) return { visibleCount: total, hiddenCount: 0, step: 0 };
  const visibleCount = Math.min(total, Math.max(requested, page));
  const hiddenCount = total - visibleCount;

  return { page, visibleCount, hiddenCount, step: Math.min(page, hiddenCount) };
};

const prefersReducedMotion = (): boolean => typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Scroll only the nearest scroller where supported; jsdom and older engines may lack the method. */
const scrollRowIntoView = (element: HTMLElement | null | undefined, block: 'start' | 'nearest'): void => {
  if (!element || typeof element.scrollIntoView !== 'function') return;
  const options: { block: 'start' | 'nearest'; behavior: 'auto' | 'smooth'; container: 'nearest' } = {
    block, behavior: prefersReducedMotion() ? 'auto' : 'smooth', container: 'nearest',
  };

  announceReaderScrollIntent(element);
  element.scrollIntoView(options);
};

/**
 * Two frames later: a timeline that follows its own growth reacts to the new
 * rows on the next frame, and a jump started before that would be cut short.
 */
const afterGrowthSettles = (callback: () => void): (() => void) => {
  if (typeof globalThis.requestAnimationFrame !== 'function') {
    callback();

    return () => undefined;
  }
  let frame = 0;

  frame = globalThis.requestAnimationFrame(() => { frame = globalThis.requestAnimationFrame(callback); });

  return () => globalThis.cancelAnimationFrame(frame);
};

/** Keyboard lands on the first new row's control (checkbox, else its title); the page follows. */
const landOnRevealedRow = (section: HTMLElement, index: number): (() => void) => {
  const row = section.querySelector('.phase-task-picker__list')?.children.item(index) as HTMLElement | null;
  const control = row?.querySelector<HTMLElement>(ROW_FOCUS_SELECTOR)
    ?? section.querySelector<HTMLElement>(TOGGLE_SELECTOR);

  control?.focus({ preventScroll: true });

  return afterGrowthSettles(() => scrollRowIntoView(row, 'start'));
};

/** After "Show less" the list is shorter: keep its toggle row, and focus, in view. */
const landOnToggle = (section: HTMLElement): undefined => {
  const toggle = section.querySelector<HTMLElement>(TOGGLE_SELECTOR);

  toggle?.focus({ preventScroll: true });
  scrollRowIntoView(toggle, 'nearest');

  return undefined;
};

const useRowRevealLanding = (
  sectionRef: React.RefObject<HTMLElement | null>,
  pendingRef: React.MutableRefObject<RowReveal | null>,
  visibleCount: number,
): void => {
  useLayoutEffect(() => {
    const reveal = pendingRef.current;
    const section = sectionRef.current;

    pendingRef.current = null;
    if (!reveal || !section) return undefined;

    return reveal.kind === 'more' ? landOnRevealedRow(section, reveal.from) : landOnToggle(section);
  }, [pendingRef, sectionRef, visibleCount]);
};

/**
 * A double-click on "Show N more" lands its second click on whichever revealed
 * row slid under the pointer; swallowing it keeps that row's selection (and its
 * View) untouched. Keyboard activation (detail 0) and later clicks pass through.
 */
const swallowFollowUpClick = (event: React.MouseEvent, revealedAt: number): void => {
  if (event.detail < 2 || Date.now() - revealedAt > FOLLOW_UP_CLICK_MS) return;
  event.preventDefault();
  event.stopPropagation();
};

/** Reveals rows a page at a time; rows added later stay behind the counter until asked for. */
const useTaskRowWindow = (
  items: TaskChecklistItem[],
  limit: number | undefined,
  sectionRef: React.RefObject<HTMLElement | null>,
) => {
  const page = normalizePageSize(limit);
  const [requested, setRequested] = useState(page ?? 0);
  const pendingRef = useRef<RowReveal | null>(null);
  const revealedAt = useRef(0);
  const view = taskRowWindow(items.length, requested, page);

  useRowRevealLanding(sectionRef, pendingRef, view.visibleCount);

  return {
    ...view,
    visibleItems: view.hiddenCount ? items.slice(0, view.visibleCount) : items,
    guardRevealedRows: (event: React.MouseEvent) => swallowFollowUpClick(event, revealedAt.current),
    showMore: () => {
      pendingRef.current = { kind: 'more', from: view.visibleCount };
      revealedAt.current = Date.now();
      setRequested(view.visibleCount + view.step);
    },
    showLess: () => {
      pendingRef.current = { kind: 'less' };
      setRequested(page ?? 0);
    },
  };
};

const taskNoun = (count: number): string => (count === 1 ? 'task' : 'tasks');

const TaskChecklistShowMore: React.FC<{
  view: TaskRowWindow;
  listId: string;
  onShowMore: () => void;
}> = ({ view: { step, hiddenCount }, listId, onShowMore }) => (
  <button type="button" className="phase-task-picker__more" aria-expanded={false} aria-controls={listId}
    aria-label={`Show ${step} more ${taskNoun(step)}${hiddenCount > step ? `, ${hiddenCount} remaining` : ''}`}
    onClick={onShowMore}>
    <i className="fa-solid fa-chevron-down" aria-hidden="true" />
    <span className="phase-task-picker__more-label">
      {`Show ${step} more`}
      {hiddenCount > step && <span className="phase-task-picker__more-count">{` · ${hiddenCount} left`}</span>}
    </span>
  </button>
);

const TaskChecklistMoreToggles: React.FC<{
  view: TaskRowWindow;
  listId: string;
  onShowMore: () => void;
  onShowLess: () => void;
}> = ({ view, listId, onShowMore, onShowLess }) => (
  <div className="phase-task-picker__more-row">
    {view.hiddenCount > 0 && <TaskChecklistShowMore view={view} listId={listId} onShowMore={onShowMore} />}
    {view.page !== undefined && view.visibleCount > view.page && (
      <button type="button" className="phase-task-picker__less" aria-controls={listId}
        aria-label={`Show less, first ${view.page} ${taskNoun(view.page)} only`} onClick={onShowLess}>
        <i className="fa-solid fa-chevron-up" aria-hidden="true" />
        <span>Show less</span>
      </button>
    )}
  </div>
);

/** Shared original task picker presentation; its caller owns selection or execution state. */
export const TaskChecklist: React.FC<TaskChecklistProps> = ({
  ariaLabel, heading, headingAction, totals, items, completionOnly = false, onSelectionChange, onViewTask,
  initialVisibleCount,
}) => {
  const listId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const rows = useTaskRowWindow(items, initialVisibleCount, sectionRef);
  const toggles = rows.page !== undefined && (rows.hiddenCount > 0 || rows.visibleCount > rows.page);

  return (
    <section ref={sectionRef} className="phase-task-picker" aria-label={ariaLabel}>
      <div className="phase-task-picker__heading">
        <span>{heading}</span>
        <span className="phase-task-picker__totals" aria-live="polite">{totals}</span>
        {headingAction}
      </div>
      <TaskChecklistRows id={toggles ? listId : undefined} items={rows.visibleItems}
        onClickCapture={rows.page === undefined ? undefined : rows.guardRevealedRows}
        completionOnly={completionOnly} onSelectionChange={onSelectionChange} onViewTask={onViewTask} />
      {toggles && (
        <TaskChecklistMoreToggles view={rows} listId={listId} onShowMore={rows.showMore} onShowLess={rows.showLess} />
      )}
    </section>
  );
};
